import { UnitAccountViewModel } from '@ail/customers-api'
import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useMemo, useState } from 'react'
import DataGrid, { Column } from 'react-data-grid'
import { useTranslation } from 'react-i18next'

import IdIsCopied from '../../../../helpers/IdIsCopied'
import { useGetUnitsQuery } from '../../../../redux/api/customerApi'
import { useAppSelector } from '../../../../redux/store'
import './ShowUnits.scss'
import './ShowUnitsFunctional.scss'
import { Loader } from '../../../common/Loader/Loader'

interface Props {
  tenantId: string
  customerId: string
  onClose: () => void
}

export default function ShowUnitsFunctional({ tenantId, customerId, onClose }: Props) {
  const { t } = useTranslation('common')
  const currentUser = useAppSelector((s) => s.appData.currentUser)

  const [searchString, setSearchString] = useState('')
  const [idCopied, setIdCopied] = useState(false)
  const [copiedPopUpToggle, setCopiedPopUpToggle] = useState(false)

  const { data: units = [], isLoading } = useGetUnitsQuery(
    tenantId && customerId ? { tenantId, customerId } : skipToken
  )

  useEffect(() => {
    if (idCopied) {
      const timer = setTimeout(() => setIdCopied(false), 4000)
      return () => clearTimeout(timer)
    } else {
      const timer = setTimeout(() => setCopiedPopUpToggle(false), 500)
      return () => clearTimeout(timer)
    }
  }, [idCopied])

  const copyId = (id: string) => {
    navigator.clipboard.writeText(id)
    setIdCopied(true)
    setCopiedPopUpToggle(true)
  }

  const cols = useMemo<Column<UnitAccountViewModel>[]>(
    () => [
      {
        key: 'name',
        name: t('NAME'),
        renderCell({ row }) {
          return (
            <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
              {currentUser?.isAdmin && <i onClick={() => copyId(row.id)} className="icon-id" />}
              {row.name || t('NO_COST_CENTER')}
            </div>
          )
        },
      },
      { key: 'description', name: t('DESCRIPTION') },
      { key: 'notes', name: t('NOTE') },
    ],
    [t, currentUser]
  )

  const visibleRows = useMemo(
    () => (searchString ? units.filter((u) => u.name.toLowerCase().includes(searchString.toLowerCase())) : units),
    [units, searchString]
  )

  return (
    <>
      <div className="showUnits">
        <Loader blocking={isLoading}>
          <a className="showUnits__close" onClick={onClose}>
            <i className="icon-close" />
          </a>
          <div className="showUnits__header">
            <div className="showUnits__title">
              <i className="icon-assign" />
              {t('UNITS')}
            </div>
            <div className="showUnits__search">
              <input
                autoFocus
                type="text"
                autoComplete="off"
                value={searchString}
                placeholder={t('FILTER_UNIT_BY_KEYWORD')}
                onChange={(e) => setSearchString(e.currentTarget.value)}
              />
              <button title={t('CLEAR')} onClick={() => setSearchString('')}>
                <span className="icon-clear" />
              </button>
              <i className="icon-search" />
            </div>
          </div>
          <div className="showUnits__grid">
            <DataGrid className="rdg-light" columns={cols} rows={visibleRows} rowHeight={45} />
          </div>
        </Loader>
        {copiedPopUpToggle && <IdIsCopied idCopied={idCopied} />}
      </div>
      <div className="blur" />
    </>
  )
}
